import { Badge, Button, cn, Logo, Textarea } from "@qn/ui";
import {
  AlertTriangle,
  Brain,
  Check,
  ChevronDown,
  Copy,
  MoreHorizontal,
  Paperclip,
  Pencil,
  RefreshCw,
  Repeat,
  Square,
  ThumbsDown,
  ThumbsUp,
  Volume2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type Message, type PickerModel, textOf } from "../lib/api";
import type { LiveStream } from "../lib/chat-store";
import { formatCost, useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import {
  chunkForSpeech,
  createSpeaker,
  DEFAULT_VOICE,
  type Speaker,
  unlockAudio,
  type VoiceSettings,
} from "../lib/voice";
import { DelegateCard, ToolCard, type ToolState } from "./AgentParts";
import { ArtifactCard } from "./ArtifactCard";
import { Markdown } from "./Markdown";
import { ModelMenu } from "./ModelPicker";

function IconButton({
  label,
  onClick,
  children,
  "aria-expanded": expanded,
}: {
  label: string;
  onClick?: () => void;
  children: React.ReactNode;
  "aria-expanded"?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-expanded={expanded}
      onClick={onClick}
      className="grid size-7 place-items-center rounded-md text-[var(--fg-faint)] transition hover:bg-[var(--surface-3)] hover:text-[var(--fg)] [&_svg]:size-3.5"
    >
      {children}
    </button>
  );
}

function CopyButton({ text }: { text: string }) {
  const t = useT();
  const [done, setDone] = useState(false);
  return (
    <IconButton
      label={t("action.copy")}
      onClick={() => {
        navigator.clipboard?.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? <Check /> : <Copy />}
    </IconButton>
  );
}

/** Reads a message aloud in its own language and, for Arabic, in the dialect the user writes in. */
function ListenButton({
  text,
  voice,
  userSample,
}: {
  text: string;
  voice: VoiceSettings;
  userSample?: string;
}) {
  const t = useT();
  const [playing, setPlaying] = useState(false);
  const sp = useRef<Speaker | null>(null);
  useEffect(() => () => sp.current?.cancel(), []);
  const toggle = () => {
    if (playing) {
      sp.current?.cancel();
      setPlaying(false);
      return;
    }
    unlockAudio();
    // Always match the voice to the text's language/dialect (never a fixed one), via the server voice when configured.
    const s = createSpeaker({ ...voice.tts, auto: true }, undefined, userSample);
    sp.current = s;
    setPlaying(true);
    for (const c of chunkForSpeech(text)) s.speak(c);
    void s.idle().then(() => setPlaying(false));
  };
  return (
    <IconButton label={playing ? t("msg.stopListening") : t("msg.listen")} onClick={toggle}>
      {playing ? <Square /> : <Volume2 />}
    </IconButton>
  );
}

function Thinking({ text, live }: { text: string; live?: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(!!live);
  if (!text) return null;
  return (
    <div className="mb-3 rounded-xl border border-[var(--line)] bg-[var(--surface-2)]/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs text-[var(--fg-muted)]"
        aria-expanded={open}
      >
        <Brain className="size-3.5 text-[var(--accent-text)]" />{" "}
        {live ? t("msg.thinking") : t("msg.thoughts")}
        <ChevronDown className={cn("ms-auto size-3.5 transition", open && "rotate-180")} />
      </button>
      {open && (
        <div
          dir="auto"
          className="max-h-64 overflow-y-auto whitespace-pre-wrap border-t border-[var(--line)] px-3 py-2 text-xs leading-relaxed text-[var(--fg-muted)]"
        >
          {text}
        </div>
      )}
    </div>
  );
}

function Avatar() {
  return <Logo size={26} className="mt-0.5 shrink-0" />;
}

export function UserMessage({
  m,
  onEdit,
  busy,
}: {
  m: Message;
  onEdit: (id: string, text: string) => void;
  busy: boolean;
}) {
  const t = useT();
  const text = textOf(m.content);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  return (
    <div className="animate-rise group flex flex-col items-end gap-1">
      {editing ? (
        <div className="w-full max-w-[85%] space-y-2">
          <Textarea
            dir="auto"
            rows={Math.min(10, draft.split("\n").length + 1)}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" onClick={() => setEditing(false)}>
              {t("action.cancel")}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!draft.trim() || busy}
              onClick={() => {
                setEditing(false);
                onEdit(m.id, draft.trim());
              }}
            >
              {t("msg.saveSend")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {m.content.some((p) => p.type === "file") && (
            <div className="flex max-w-[85%] flex-wrap justify-end gap-1.5">
              {m.content.map((p) =>
                p.type !== "file" ? null : /^image\/(png|jpeg|webp|gif)$/.test(p.mime) ? (
                  <img
                    key={p.fileId}
                    src={`/api/files/${p.fileId}?inline=1`}
                    alt={p.name}
                    className="max-h-48 max-w-full rounded-xl border border-[var(--line)] object-cover"
                  />
                ) : (
                  <a
                    key={p.fileId}
                    href={`/api/files/${p.fileId}`}
                    download={p.name}
                    className="flex items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--surface-2)] px-3 py-2 text-xs hover:bg-[var(--surface-3)]"
                  >
                    <Paperclip className="size-3.5 text-[var(--fg-muted)]" />
                    <span className="max-w-48 truncate" dir="auto">
                      {p.name}
                    </span>
                    <span className="text-[var(--fg-faint)]">
                      {Math.max(1, Math.round(p.size / 1024))} KB
                    </span>
                  </a>
                ),
              )}
            </div>
          )}
          <div
            dir="auto"
            className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-ee-md bg-[var(--surface-3)] px-4 py-2.5 text-[0.97rem] leading-relaxed"
          >
            {text}
          </div>
          <div className="flex gap-0.5 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100 max-lg:opacity-100">
            <CopyButton text={text} />
            {!busy && (
              <IconButton
                label={t("msg.edit")}
                onClick={() => {
                  setDraft(text);
                  setEditing(true);
                }}
              >
                <Pencil />
              </IconButton>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export interface ToolInfo {
  results: Map<string, { content: string; isError: boolean }>;
  live: Record<string, "running" | "awaiting_approval" | "denied">;
  subs?: Record<
    string,
    { agent: string; icon: string; task: string; state: string; detail?: string }
  >;
}

export function AssistantMessage({
  m,
  isLast,
  busy,
  models,
  onRegenerate,
  tools,
  voice = DEFAULT_VOICE,
  userSample,
  onFollowUp,
  rating,
  onRate,
}: {
  m: Message;
  isLast: boolean;
  busy: boolean;
  models: PickerModel[];
  onRegenerate: (model?: PickerModel) => void;
  tools?: ToolInfo;
  voice?: VoiceSettings;
  userSample?: string;
  /** One-tap follow-ups under the latest answer (shorter, simpler, translate…). */
  onFollowUp?: (prompt: string) => void;
  /** 1 = liked, -1 = disliked, undefined = not rated. */
  rating?: number;
  onRate?: (value: 1 | -1 | 0, reason?: string) => void;
}) {
  const t = useT();
  const showUsage = usePrefs((s) => s.showUsage);
  // Which model answered (and token use) stays hidden until the user taps the "⋯" button.
  const [details, setDetails] = useState(false);
  const text = textOf(m.content);
  const thinking = textOf(m.content, "thinking");
  return (
    <div className="animate-rise group flex gap-3">
      <Avatar />
      <div className="min-w-0 flex-1">
        <Thinking text={thinking} />
        {m.content.some((p) => p.type === "tool_call") ? (
          m.content.map((p, i) => {
            if (p.type === "text") return <Markdown key={`t${i}`} text={p.text} />;
            if (p.type !== "tool_call") return null;
            const r = tools?.results.get(p.id);
            const state: ToolState = r
              ? r.isError
                ? "error"
                : "done"
              : (tools?.live[p.id] ?? "running");
            if (p.name === "delegate") {
              const task = String((p.input as { task?: string })?.task ?? "").slice(0, 160);
              const sub = Object.values(tools?.subs ?? {}).find((x) => x.task === task);
              return (
                <DelegateCard
                  key={p.id}
                  input={(p.input ?? {}) as Record<string, unknown>}
                  state={state}
                  result={r?.content}
                  live={sub}
                />
              );
            }
            if (p.name === "create_artifact" || p.name === "update_artifact")
              return <ArtifactCard key={p.id} call={p} result={r?.content} state={state} />;
            return (
              <ToolCard
                key={p.id}
                name={p.name}
                input={(p.input ?? {}) as Record<string, unknown>}
                state={state}
                result={r?.content}
              />
            );
          })
        ) : text ? (
          <Markdown text={text} />
        ) : (
          m.status !== "error" && <p className="text-sm text-[var(--fg-faint)]">{t("msg.empty")}</p>
        )}
        {m.status === "error" && <ErrorNote message={m.error ?? t("msg.error")} />}
        {m.status === "aborted" && (
          <p className="mt-2 text-xs text-[var(--fg-faint)]">{t("msg.stopped")}</p>
        )}
        {details && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {m.model && (
              <Badge
                tone="neutral"
                title={`${m.providerName} · ${m.model}`}
                className="max-w-[16rem]"
              >
                <span className="truncate">
                  {m.providerName} · {m.model}
                </span>
              </Badge>
            )}
            {showUsage && m.outputTokens > 0 && (
              <span className="text-[11px] text-[var(--fg-faint)]" dir="ltr">
                {m.inputTokens}↑ {m.outputTokens}↓{m.costUsd > 0 && ` · ${formatCost(m.costUsd)}`}
              </span>
            )}
          </div>
        )}
        {rating === -1 && onRate && (
          <div
            role="group"
            aria-label={t("feedback.why")}
            className="mt-2 flex flex-wrap items-center gap-1.5"
          >
            <span className="text-xs text-[var(--fg-faint)]">{t("feedback.why")}</span>
            {(["wrong", "too_long", "language", "slow", "unsafe", "other"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => onRate(-1, k)}
                className="rounded-full border border-[var(--line)] px-2.5 py-0.5 text-xs text-[var(--fg-muted)] hover:border-[var(--accent)] hover:text-[var(--fg)]"
              >
                {t(`feedback.reason.${k}`)}
              </button>
            ))}
          </div>
        )}
        {isLast && !busy && onFollowUp && text && (
          <div role="group" className="mt-2 flex flex-wrap gap-1.5" aria-label={t("quick.label")}>
            {(["shorter", "simpler", "more", "translate", "continue"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => onFollowUp(t(`quick.${k}.prompt`))}
                className="rounded-full border border-[var(--line)] px-3 py-1 text-xs text-[var(--fg-muted)] transition hover:border-[var(--accent)] hover:text-[var(--fg)]"
              >
                {t(`quick.${k}`)}
              </button>
            ))}
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <div className="ms-auto flex gap-0.5 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100 max-lg:opacity-100">
            <CopyButton text={text} />
            {text && <ListenButton text={text} voice={voice} userSample={userSample} />}
            {text && onRate && (
              <>
                <IconButton
                  label={t("feedback.good")}
                  aria-expanded={rating === 1}
                  onClick={() => onRate(rating === 1 ? 0 : 1)}
                >
                  <ThumbsUp className={rating === 1 ? "fill-current text-[var(--success)]" : ""} />
                </IconButton>
                <IconButton
                  label={t("feedback.bad")}
                  aria-expanded={rating === -1}
                  onClick={() => onRate(rating === -1 ? 0 : -1)}
                >
                  <ThumbsDown
                    className={rating === -1 ? "fill-current text-[var(--danger)]" : ""}
                  />
                </IconButton>
              </>
            )}
            {m.model && (
              <IconButton
                label={t("msg.details")}
                onClick={() => setDetails((v) => !v)}
                aria-expanded={details}
              >
                <MoreHorizontal />
              </IconButton>
            )}
            {isLast && !busy && (
              <>
                <IconButton label={t("msg.regenerate")} onClick={() => onRegenerate()}>
                  <RefreshCw />
                </IconButton>
                <ModelMenu
                  models={models}
                  value={
                    m.providerId && m.model ? { providerId: m.providerId, model: m.model } : null
                  }
                  onPick={(pm) => onRegenerate(pm)}
                  align="end"
                >
                  <button
                    type="button"
                    title={t("msg.retryWith")}
                    aria-label={t("msg.retryWith")}
                    className="grid size-7 place-items-center rounded-md text-[var(--fg-faint)] transition hover:bg-[var(--surface-3)] hover:text-[var(--fg)] [&_svg]:size-3.5"
                  >
                    <Repeat />
                  </button>
                </ModelMenu>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function LiveAssistantMessage({ s }: { s: LiveStream }) {
  const t = useT();
  return (
    <div className="flex gap-3">
      <Avatar />
      <div className="min-w-0 flex-1">
        {s.fallbacks.length > 0 && (
          // One quiet line instead of a red banner per failed model; the details are one tap away.
          <details className="mb-2 rounded-lg bg-[color-mix(in_oklab,var(--warn)_10%,transparent)] px-3 py-1.5 text-xs text-[var(--warn)]">
            <summary className="flex cursor-pointer list-none items-center gap-2">
              <AlertTriangle className="size-3.5 shrink-0" />
              <span dir="auto">{t("msg.fallbackSummary", { n: s.fallbacks.length })}</span>
            </summary>
            <ul className="mt-1.5 space-y-1 border-t border-[var(--line)] pt-1.5">
              {s.fallbacks.map((f) => (
                <li key={`${f.fromModel}>${f.toModel}`} dir="auto">
                  {t("msg.fallback", {
                    from: f.fromModel,
                    to: f.toModel,
                    reason: f.reason.slice(0, 120),
                  })}
                </li>
              ))}
            </ul>
          </details>
        )}
        <Thinking text={s.thinking} live />
        {s.text ? (
          <div className={s.running ? "caret" : ""}>
            <Markdown text={s.text} />
          </div>
        ) : (
          s.running &&
          !s.error && (
            <div
              className="flex items-center gap-1.5 py-2"
              role="status"
              aria-label={t("msg.thinking")}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="size-2 animate-bounce rounded-full bg-[var(--accent)]"
                  style={{ animationDelay: `${i * 120}ms` }}
                />
              ))}
            </div>
          )
        )}
        {s.error && <ErrorNote message={s.error} />}
      </div>
    </div>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <div
      role="alert"
      dir="auto"
      className="mt-2 flex items-start gap-2 rounded-lg bg-[color-mix(in_oklab,var(--danger)_12%,transparent)] px-3 py-2 text-sm text-[var(--danger)]"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span className="break-words">{message}</span>
    </div>
  );
}
